// SPEC manna-spec.md §2.2.6 + manna-web-ui.md §3.14 — UI-side lazy MFA
// step-up, action-triggered only (locked 2026-05-02).
//
// Wraps the authenticated app shell. For an internal-role user (anything
// other than partner_admin / partner_rep) whose JWT has is_mfa_active=false,
// surfaces a blocking <MfaStepUpDialog> ONLY when the user actively tries
// to do something privileged. Two trigger paths:
//
//   1. Route-scoped: the user lands on a route in the MFA-required
//      allow-list (admin pages, agentic surface, deal registrations
//      approval, etc.). The dialog blocks the page.
//   2. Action-scoped (handled elsewhere): Mode-A agent invokes and other
//      sensitive actions raise FreshMfaDialog at the moment of
//      submission — see apps/web/src/features/agents/FreshMfaDialog.tsx.
//
// What this component does NOT do: it never *blocks* off-list routes. The
// blocking MfaStepUpDialog only fires on allow-listed routes. Off-list,
// read-heavy CRM pages still render their content; RLS silently denies
// internal-role reads when is_mfa_active=false, so they degrade to "lists
// may be empty." Those pages now carry a subtle, non-blocking hint that
// explains the emptiness — see components/MfaSessionHint.tsx, rendered in
// Layout (spec §2.2.6, revised 2026-06-04). The exported predicates below
// (hasInternalAppRole / routeRequiresSessionMfa) are reused by that hint so
// the "restricted but off-list" decision stays single-sourced.
//
// Cancel semantics: cancelling on an allow-list route bounces the user
// back to /dashboard (so the dialog actually closes — otherwise
// allowListed=true would re-mount it on the next render). No sign-out.
// The user can return to the privileged route any time and will be
// re-prompted; nothing is "dismissed" or "remembered" past the cancel.
//
// /security is special: it's the elevation surface for TOTP enrollment.
// The dialog is suppressed there so the page itself can render the
// enroll UI.
//
// Partner roles fall through unchanged — partner_admin / partner_rep are
// baseline-MFA-exempt (manna-spec §1.4.3), and the partner portal layout
// is a separate top-level route group not wrapped by this component.

import { useLocation, useNavigate } from "react-router-dom";
import { useAuthStore, type AppRole } from "../stores/auth";
import { paths } from "../routes/paths";
import { MfaStepUpDialog } from "./MfaStepUpDialog";

const EXTERNAL_MANNA_ROLES = new Set<string>(["partner_admin", "partner_rep"]);

export function hasInternalAppRole(appRoles: AppRole[]): boolean {
  return appRoles.some((r) => r.app === "manna" && !EXTERNAL_MANNA_ROLES.has(r.role));
}

// Pathname → "this route requires session MFA to be useful at all."
// Keep the list explicit and small; add entries when a new surface lands
// whose primary purpose is to drive a manna-spec §2.2.8 action.
//
// /security is intentionally NOT here — it's handled as a hard exemption
// below (the page itself is the elevation surface).
export function routeRequiresSessionMfa(pathname: string): boolean {
  if (pathname === paths.security) return false;
  return (
    pathname.startsWith("/admin") ||
    pathname.startsWith("/agents") ||
    pathname.startsWith("/conversations") ||
    pathname.startsWith("/runs") ||
    pathname.startsWith("/approvals") ||
    pathname.startsWith("/skills") ||
    pathname.startsWith("/templates") ||
    pathname === paths.dealRegistrations
  );
}

interface Props {
  children: React.ReactNode;
}

export function RequireInternalMfa({ children }: Props) {
  const location = useLocation();
  const navigate = useNavigate();
  const appRoles = useAuthStore((s) => s.appRoles);
  const isMfaActive = useAuthStore((s) => s.isMfaActive);

  const isSecurityRoute = location.pathname === paths.security;
  const internal = hasInternalAppRole(appRoles);
  const restricted = !isMfaActive && internal && !isSecurityRoute;
  const allowListed = restricted && routeRequiresSessionMfa(location.pathname);

  return (
    <>
      {children}
      <MfaStepUpDialog
        open={allowListed}
        title="Verify it's you to access Manna"
        description="Your account has internal-role privileges that require multi-factor authentication. Verify now to unlock access."
        cancelLabel="Cancel"
        onSuccess={() => {
          // is_mfa_active flips via the auth store's onAuthStateChange
          // listener after MFA verifies; nothing to do here.
        }}
        onCancel={() => {
          // Bounce off the allow-list route so allowListed flips to false
          // and the dialog actually unmounts. No sign-out; sign-out lives
          // in the user menu.
          navigate(paths.dashboard);
        }}
        onEnroll={() => {
          // Enroll button navigates to /security via RouterLink; no extra
          // state to reset now that the banner is gone.
        }}
      />
    </>
  );
}
